import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Chip,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { DataGrid, deDE, GridColDef } from '@mui/x-data-grid';

import {
  ArnoMappingStatus,
  ArnoSourceType,
  IArnoMapping,
  IArnoMappingRef,
  useArnoMappingFamilies,
  useArnoMappings,
} from '../../hooks/reactQuerys/ArnoAdminQueries';
import {
  mappingStatusChip,
  MAPPING_STATUS_LABEL,
  SOURCE_TYPE_LABEL,
} from './arnoAdminLabels';
import { FamilyRulePanel } from './FamilyRulePanel';
import { FieldImpactPanel } from './FieldImpactPanel';
import { MappingDecisionDialog } from './MappingDecisionDialog';

/**
 * Zuordnungen zwischen ArnoTop-Variablen und DiNo-Feldern (#1245).
 *
 * Der echte Bestand hat über 3 000 Zeilen, deshalb liegen Suche, Filter und
 * Seiten auf dem Server. Es gibt bewusst keine Massenaktion: 1 213 Variablen
 * „alle bestätigen" wäre genau das automatische Raten, das hier nicht passieren
 * soll.
 *
 * Zwei Sichten auf dieselben Daten: die Hebelwirkung sagt, *welches* Feld sich
 * zuerst lohnt, die vollständige Liste bleibt für alles andere. Die Reihenfolge
 * der Tabs ist die empfohlene Reihenfolge der Arbeit.
 */

const STATUS_OPTIONS: ArnoMappingStatus[] = [
  'UNMAPPED',
  'SUGGESTED',
  'CONFIRMED',
  'MANUAL',
  'IGNORED',
];

const SOURCE_OPTIONS: ArnoSourceType[] = [
  'DOCVARIABLE',
  'REGISTER',
  'FIELD',
  'TRANSACTION_TYPE',
  'BLOCK_REFERENCE',
];

export const MappingTab: React.FC = () => {
  const [view, setView] = useState<'impact' | 'families' | 'all'>('impact');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<ArnoMappingStatus[]>([]);
  const [sourceType, setSourceType] = useState<ArnoSourceType[]>([]);
  const [family, setFamily] = useState('');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [selected, setSelected] = useState<IArnoMappingRef | null>(null);

  // Suche entprellen und Seite zurücksetzen — sonst steht man auf Seite 12 einer
  // Trefferliste mit drei Zeilen.
  useEffect(() => {
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setPage(0);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const { mappingFamilies } = useArnoMappingFamilies();
  const { mappingPage, isFetchingMappings } = useArnoMappings({
    page,
    pageSize,
    search,
    status: status.length > 0 ? status : undefined,
    sourceType: sourceType.length > 0 ? sourceType : undefined,
    family: family || undefined,
  });

  const rows = useMemo(() => mappingPage?.rows ?? [], [mappingPage]);

  const columns: GridColDef[] = [
    {
      field: 'sourceKey',
      headerName: 'Quelle in ArnoTop',
      flex: 2,
      minWidth: 200,
      renderCell: parameters => {
        const row = parameters.row as IArnoMapping;
        return (
          <Box>
            <Typography variant='body2'>{row.sourceKey}</Typography>
            <Typography variant='caption' color='text.secondary'>
              {SOURCE_TYPE_LABEL[row.sourceType] ?? row.sourceType}
              {row.context?.family ? ` · ${row.context.family}` : ''}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: 'targetPath',
      headerName: 'DiNo-Zielfeld',
      flex: 2,
      minWidth: 200,
      renderCell: parameters =>
        parameters.row.targetPath ? (
          <Typography variant='body2'>{parameters.row.targetPath}</Typography>
        ) : (
          <Typography variant='body2' color='text.secondary'>
            –
          </Typography>
        ),
    },
    {
      field: 'status',
      headerName: 'Stand',
      width: 170,
      renderCell: parameters => {
        const row = parameters.row as IArnoMapping;
        const chip = mappingStatusChip(row.status);
        return (
          <Tooltip
            title={
              row.isHuman
                ? 'Von Hand entschieden — überlebt jeden Import.'
                : 'Automatisch ermittelt — ein Import darf das ändern.'
            }
          >
            <Chip size='small' color={chip.color} label={chip.label} />
          </Tooltip>
        );
      },
    },
    {
      field: 'occurrences',
      headerName: 'Vorkommen',
      width: 120,
      align: 'right',
      headerAlign: 'right',
      valueGetter: parameters =>
        (parameters.row as IArnoMapping).context?.occurrences ?? 0,
    },
  ];

  return (
    <Box>
      <Tabs
        value={view}
        onChange={(_event, next) =>
          setView(next as 'impact' | 'families' | 'all')
        }
        sx={{ mb: 2 }}
      >
        <Tab value='impact' label='Nach Hebelwirkung' />
        <Tab value='families' label='Familienregeln' />
        <Tab value='all' label='Alle Zuordnungen' />
      </Tabs>

      {view === 'impact' && <FieldImpactPanel />}

      {view === 'families' && <FamilyRulePanel />}

      {view === 'all' && (
        <Box>
          <Stack
            direction='row'
            spacing={2}
            alignItems='center'
            sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}
          >
            <TextField
              size='small'
              label='Suche (Quelle, Ziel, Notiz)'
              value={searchInput}
              onChange={event => setSearchInput(event.target.value)}
              sx={{ minWidth: 260 }}
            />
            <TextField
              size='small'
              select
              label='Stand'
              value={status}
              onChange={event =>
                setStatus(
                  (typeof event.target.value === 'string'
                    ? [event.target.value]
                    : event.target.value) as ArnoMappingStatus[]
                )
              }
              SelectProps={{ multiple: true }}
              sx={{ minWidth: 200 }}
            >
              {STATUS_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>
                  {MAPPING_STATUS_LABEL[option]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              select
              label='Art der Quelle'
              value={sourceType}
              onChange={event =>
                setSourceType(
                  (typeof event.target.value === 'string'
                    ? [event.target.value]
                    : event.target.value) as ArnoSourceType[]
                )
              }
              SelectProps={{ multiple: true }}
              sx={{ minWidth: 200 }}
            >
              {SOURCE_OPTIONS.map(option => (
                <MenuItem key={option} value={option}>
                  {SOURCE_TYPE_LABEL[option]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              size='small'
              select
              label='Variablenfamilie'
              value={family}
              onChange={event => {
                setFamily(event.target.value);
                setPage(0);
              }}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value=''>Alle</MenuItem>
              {mappingFamilies.map(entry => (
                <MenuItem key={entry.family} value={entry.family}>
                  {entry.family} ({entry.open} von {entry.total} offen)
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          <DataGrid
            autoHeight
            localeText={deDE.components.MuiDataGrid.defaultProps.localeText}
            rows={rows}
            columns={columns}
            getRowId={row => row.idVariableMapping}
            loading={isFetchingMappings}
            paginationMode='server'
            rowCount={mappingPage?.total ?? 0}
            paginationModel={{ page, pageSize }}
            onPaginationModelChange={model => {
              setPage(model.page);
              setPageSize(model.pageSize);
            }}
            pageSizeOptions={[25, 50, 100]}
            disableRowSelectionOnClick
            onRowClick={parameters => {
              const row = parameters.row as IArnoMapping;
              setSelected({
                sourceType: row.sourceType,
                sourceKey: row.sourceKey,
                scope: row.scope,
              });
            }}
            sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
          />

          <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
            Eine Zeile anklicken, um die Zuordnung zu entscheiden. Es gibt
            bewusst keine Sammelbestätigung — jede Zuordnung ist eine eigene
            fachliche Aussage.
          </Typography>
        </Box>
      )}

      <MappingDecisionDialog
        mapping={selected}
        onClose={() => setSelected(null)}
      />
    </Box>
  );
};
